/* factory-graphics-loader.js - lazy manifest and image cache for factory graphics. */
(function () {
  const Manifest = window.FactoryGraphicsManifest || {};
  const manifestCache = new Map();
  const bindingsCache = new Map();
  const imageCache = new Map();
  let activeQuality = Manifest.mergeQualitySettings ? Manifest.mergeQualitySettings() : { level: "balanced" };

  function object(value) {
    return value && typeof value === "object" && !Array.isArray(value) ? value : {};
  }

  function cleanString(value, fallback) {
    const clean = String(value || "").trim();
    return clean || fallback || "";
  }

  function manifestUrl(name) {
    return Manifest.manifestUrl ? Manifest.manifestUrl(name) : `/assets/factory/manifests/${encodeURIComponent(cleanString(name, "production-v1"))}.json`;
  }

  function bindingsUrl(name) {
    return Manifest.bindingsUrl ? Manifest.bindingsUrl(name) : `/assets/factory/bindings/${encodeURIComponent(cleanString(name, "default"))}.json`;
  }

  async function fetchJson(url, fallback) {
    try {
      const response = await fetch(url, { credentials: "same-origin", cache: "no-cache" });
      if (!response.ok) return fallback;
      return await response.json();
    } catch (_) {
      return fallback;
    }
  }

  async function loadManifest(name) {
    const url = manifestUrl(name);
    if (manifestCache.has(url)) return manifestCache.get(url);
    const promise = fetchJson(url, {}).then((json) => {
      const normalized = Manifest.normalizeManifest ? Manifest.normalizeManifest(json) : json;
      manifestCache.set(url, Promise.resolve(normalized));
      return normalized;
    });
    manifestCache.set(url, promise);
    return promise;
  }

  async function loadBindings(name) {
    const url = bindingsUrl(name);
    if (bindingsCache.has(url)) return bindingsCache.get(url);
    const promise = fetchJson(url, {}).then((json) => {
      const normalized = Manifest.normalizeBindings ? Manifest.normalizeBindings(json) : json;
      bindingsCache.set(url, Promise.resolve(normalized));
      return normalized;
    });
    bindingsCache.set(url, promise);
    return promise;
  }

  function imageSourceFromManifest(manifest, keyOrUrl) {
    const clean = cleanString(keyOrUrl, "");
    if (!clean) return "";
    if (/^(https?:)?\/\//i.test(clean) || clean.startsWith("/") || clean.startsWith("data:")) return clean;
    const image = manifest && manifest.images && manifest.images[clean];
    if (image) return cleanString(image.src, "");
    return clean;
  }

  function loadImage(keyOrUrl, manifest) {
    const src = imageSourceFromManifest(manifest, keyOrUrl);
    if (!src) return Promise.resolve(null);
    if (imageCache.has(src)) return imageCache.get(src).promise;
    const entry = { src, image: null, loaded: false, failed: false, promise: null };
    entry.promise = new Promise((resolve) => {
      const img = new Image();
      img.decoding = "async";
      img.onload = () => {
        entry.image = img;
        entry.loaded = true;
        resolve(img);
      };
      img.onerror = () => {
        entry.failed = true;
        resolve(null);
      };
      img.src = src;
    });
    imageCache.set(src, entry);
    return entry.promise;
  }

  function getCachedImage(keyOrUrl, manifest) {
    const src = imageSourceFromManifest(manifest, keyOrUrl);
    const entry = src ? imageCache.get(src) : null;
    return entry && entry.loaded ? entry.image : null;
  }

  function getImageState(keyOrUrl, manifest) {
    const src = imageSourceFromManifest(manifest, keyOrUrl);
    if (!src) return { src: "", loaded: false, failed: true, image: null };
    return imageCache.get(src) || { src, loaded: false, failed: false, image: null };
  }

  function preloadImages(manifest, keys) {
    const list = Array.isArray(keys) && keys.length
      ? keys
      : Manifest.listRequiredImages ? Manifest.listRequiredImages(manifest) : Object.keys(object(manifest && manifest.images));
    return Promise.all(list.map((key) => loadImage(key, manifest))).then((images) => images.filter(Boolean));
  }

  function setQuality(settings) {
    activeQuality = Manifest.mergeQualitySettings ? Manifest.mergeQualitySettings(activeQuality, settings) : { ...activeQuality, ...object(settings) };
    return activeQuality;
  }

  function getQuality() {
    return activeQuality;
  }

  function resolveActionBinding(bindings, action, context) {
    return Manifest.resolveActionBinding ? Manifest.resolveActionBinding(bindings, action, context) : {};
  }

  function resolveEntityBinding(bindings, entity, context) {
    return Manifest.resolveEntityBinding ? Manifest.resolveEntityBinding(bindings, entity, context) : {};
  }

  async function createRuntime(options) {
    const opts = object(options);
    const manifest = await loadManifest(opts.manifest || opts.manifestName || "production-v1");
    const bindings = await loadBindings(opts.bindings || opts.bindingsName || "default");
    const quality = setQuality({ ...(manifest && manifest.quality || {}), ...object(opts.quality) });
    if (opts.preload) await preloadImages(manifest, opts.preloadImages);
    return {
      manifest,
      bindings,
      quality,
      loadImage: (key) => loadImage(key, manifest),
      getImage: (key) => getCachedImage(key, manifest),
      getImageState: (key) => getImageState(key, manifest),
      preloadImages: (keys) => preloadImages(manifest, keys),
      resolveAction: (action, context) => resolveActionBinding(bindings, action, context),
      resolveEntity: (entity, context) => resolveEntityBinding(bindings, entity, context),
      setQuality,
      getQuality,
    };
  }

  async function loadRuntime(options) {
    return createRuntime(options);
  }

  function clearCache(scope) {
    const clean = cleanString(scope, "all");
    if (clean === "all" || clean === "manifest") manifestCache.clear();
    if (clean === "all" || clean === "bindings") bindingsCache.clear();
    if (clean === "all" || clean === "images") imageCache.clear();
  }

  const api = {
    loadManifest,
    loadBindings,
    loadImage,
    getCachedImage,
    getImageState,
    preloadImages,
    setQuality,
    getQuality,
    resolveActionBinding,
    resolveEntityBinding,
    createRuntime,
    loadRuntime,
    clearCache,
  };

  window.FactoryGraphicsLoader = api;
  window.FactoryGraphics = { ...(window.FactoryGraphics || {}), loader: api };
})();
